/* ==========================================================================
   CUSTOM PRODUCT FIELD
   - Reusable Shopify line-item property fields
   - Character counter
   - Uppercase normalization
   - Text and phone validation
   - Live preview event support
   ========================================================================== */

(() => {
  const FIELD_SELECTOR = '[data-custom-product-field]';

  const sanitizeText = (value, mode) => {
    if (mode === 'phone') {
      return value.replace(/[^0-9()+\-\s]/g, '');
    }

    if (mode === 'letters_numbers') {
      return value.replace(/[^A-Za-z0-9 \-]/g, '');
    }

    return value;
  };

  const isAllowedCharacter = (character, mode) => {
    if (!character) return true;

    if (mode === 'phone') {
      return /^[0-9()+\-\s]+$/.test(character);
    }

    if (mode === 'letters_numbers') {
      return /^[A-Za-z0-9 \-]+$/.test(character);
    }

    return true;
  };

  const initialiseField = (root) => {
    if (!root || root.dataset.customFieldInitialised === 'true') return;

    const input = root.querySelector('[data-custom-field-input]');
    const counter = root.querySelector('[data-custom-field-counter]');
    const error = root.querySelector('[data-custom-field-error]');

    if (!input) return;

    root.dataset.customFieldInitialised = 'true';

    const form = input.form;
    const maxLength = Number.parseInt(input.maxLength, 10);
    const validationMode = root.dataset.validationMode || 'text';
    const shouldUppercase = input.dataset.uppercase === 'true';
    const requiredMessage =
      root.dataset.requiredMessage || 'Please complete this field.';
    const phoneMessage =
      root.dataset.phoneMessage || 'Please enter a valid phone number.';

    const updateCounter = () => {
      if (!counter || !Number.isFinite(maxLength) || maxLength < 1) return;

      const length = input.value.length;

      counter.textContent = `${length} / ${maxLength}`;
      counter.hidden = length === 0;
    };

    const updateError = () => {
      if (!error) return;

      error.textContent = input.validationMessage;
      error.hidden = input.validationMessage === '';
    };

    const validate = () => {
      const value = input.value.trim();

      input.setCustomValidity('');

      if (input.required && value === '') {
        input.setCustomValidity(requiredMessage);
      } else if (validationMode === 'phone') {
        const digitCount = (value.match(/\d/g) || []).length;

        if (digitCount < 7) {
          input.setCustomValidity(phoneMessage);
        }
      }

      input.classList.toggle('is-invalid', !input.validity.valid);
      input.setAttribute(
        'aria-invalid',
        input.validity.valid ? 'false' : 'true'
      );

      updateError();

      return input.validity.valid;
    };

    const dispatchPreviewUpdate = () => {
      root.dispatchEvent(
        new CustomEvent('custom-product-field:update', {
          bubbles: true,
          detail: {
            value: input.value,
            previewTarget: root.dataset.previewTarget || 'none',
            propertyName: root.dataset.propertyName || ''
          }
        })
      );
    };

    input.addEventListener('beforeinput', (event) => {
      if (
        event.inputType === 'insertText' &&
        event.data &&
        !isAllowedCharacter(event.data, validationMode)
      ) {
        event.preventDefault();
      }
    });

    input.addEventListener('paste', (event) => {
      const pastedValue = event.clipboardData?.getData('text');

      if (!pastedValue) return;

      const sanitizedValue = sanitizeText(pastedValue, validationMode);

      if (sanitizedValue === pastedValue) return;

      event.preventDefault();

      const start = input.selectionStart ?? input.value.length;
      const end = input.selectionEnd ?? input.value.length;

      input.setRangeText(sanitizedValue, start, end, 'end');
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });

    input.addEventListener('input', () => {
      const sanitizedValue = sanitizeText(input.value, validationMode);

      if (sanitizedValue !== input.value) {
        const cursorPosition = input.selectionStart ?? sanitizedValue.length;

        input.value = sanitizedValue;

        try {
          input.setSelectionRange(cursorPosition, cursorPosition);
        } catch (_) {}
      }

      updateCounter();
      validate();
      dispatchPreviewUpdate();
    });

    input.addEventListener('blur', () => {
      if (shouldUppercase) {
        input.value = input.value.toUpperCase().trim();
      } else {
        input.value = input.value.trim();
      }

      updateCounter();
      validate();
      dispatchPreviewUpdate();
    });

    if (form && form.dataset.customFieldsBound !== 'true') {
      form.dataset.customFieldsBound = 'true';

      form.addEventListener(
        'submit',
        (event) => {
          const fields = document.querySelectorAll(
            `${FIELD_SELECTOR} [data-custom-field-input][form="${CSS.escape(form.id)}"]`
          );

          let firstInvalidInput = null;

          fields.forEach((fieldInput) => {
            const fieldRoot = fieldInput.closest(FIELD_SELECTOR);
            const mode = fieldRoot?.dataset.validationMode || 'text';
            const uppercase = fieldInput.dataset.uppercase === 'true';

            fieldInput.value = sanitizeText(fieldInput.value, mode).trim();

            if (uppercase) {
              fieldInput.value = fieldInput.value.toUpperCase();
            }

            fieldInput.dispatchEvent(
              new Event('custom-product-field:validate', {
                bubbles: true
              })
            );

            if (!fieldInput.validity.valid && !firstInvalidInput) {
              firstInvalidInput = fieldInput;
            }
          });

          if (firstInvalidInput) {
            event.preventDefault();
            event.stopImmediatePropagation();

            firstInvalidInput.reportValidity();
            firstInvalidInput.focus({
              preventScroll: true
            });

            firstInvalidInput.scrollIntoView({
              behavior: window.matchMedia(
                '(prefers-reduced-motion: reduce)'
              ).matches
                ? 'auto'
                : 'smooth',
              block: 'center'
            });
          }
        },
        true
      );
    }

    input.addEventListener('custom-product-field:validate', validate);

    updateCounter();
    validate();
    dispatchPreviewUpdate();
  };

  const initialiseFields = (scope = document) => {
    if (scope.matches?.(FIELD_SELECTOR)) {
      initialiseField(scope);
    }

    scope.querySelectorAll?.(FIELD_SELECTOR).forEach(initialiseField);
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initialiseFields(),
      { once: true }
    );
  } else {
    initialiseFields();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialiseFields(event.target);
  });
})();